import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const EMPTY_FILTERS = { name: "", mobile: "", whatsapp: "", email: "", profession: "", place: "", program: "", batch: "", registered: "", guide: "", source: "", announce: "" };
const EDIT_KEYS = ["name", "mobile", "whatsapp", "email", "profession", "place", "program", "batch", "guide"];

export const usePastParticipants = (adminKey, onChanged) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [announceJob, setAnnounceJob] = useState(null);
  const [announcing, setAnnouncing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ pages: 1, matched: 0, start: 1 });
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const headers = { "X-PL-Client": "web", "X-Admin-Key": adminKey };

  const fetchItems = useCallback(async (query, pg = 1, f = EMPTY_FILTERS) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pg), page_size: "100" });
      if (query) params.set("q", query);
      Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch(`${API}/admin/past-participants?${params}`, { headers: { "X-PL-Client": "web", "X-Admin-Key": adminKey } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setItems(d.items || []);
      setTotal(d.total || 0);
      setPageInfo({ pages: d.pages || 1, matched: d.matched || 0, start: d.start || 1 });
      if (d.page && d.page !== pg) setPage(d.page);
    } catch (err) {
      console.warn("Could not load past participants", err);
      toast.error("Could not load past participants");
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { setPage(1); }, [q, filters]);
  useEffect(() => {
    const t = setTimeout(() => fetchItems(q.trim(), page, filters), 300);
    return () => clearTimeout(t);
  }, [q, page, filters, fetchItems]);
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await fetch(`${API}/admin/past-participants/upload`, { method: "POST", headers, body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      toast.success(`Uploaded — added: ${data.added}, updated: ${data.updated}, skipped: ${data.skipped}. Total saved: ${data.total}`);
      setPage(1);
      fetchItems(q.trim(), 1, filters);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Upload failed", { duration: 8000 });
    } finally { setUploading(false); }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () =>
    setSelected((s) => {
      const ids = items.map((p) => p.id);
      const all = ids.length > 0 && ids.every((id) => s.includes(id));
      return all ? s.filter((id) => !ids.includes(id)) : [...new Set([...s, ...ids])];
    });
  const clearSelection = () => setSelected([]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/admin/past-participants/export`, { headers });
      if (!res.ok) throw new Error();
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "positivelife_past_participants.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Past participants exported");
    } catch {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} (${p.mobile}) from Past Batch? They will be treated as a new participant if they register again.`)) return;
    setDeletingId(p.id);
    try {
      const res = await fetch(`${API}/admin/past-participants/${p.id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Removed ${p.name}`);
      setSelected((s) => s.filter((x) => x !== p.id));
      fetchItems(q.trim(), page, filters);
      onChanged?.();
    } catch {
      toast.error("Failed to remove participant");
    } finally { setDeletingId(""); }
  };

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const startEdit = (p) => {
    setEditing(p);
    setEditForm(Object.fromEntries(EDIT_KEYS.map((k) => [k, p[k] || ""])));
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/past-participants/${editing.id}`, { method: "PUT", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(editForm) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      setItems((xs) => xs.map((x) => (x.id === data.id ? data : x)));
      toast.success("Past participant updated");
      setEditing(null);
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally { setSaving(false); }
  };

  const pollJob = async (jobId) => {
    try {
      const r = await fetch(`${API}/admin/broadcasts/${jobId}`, { headers });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };

  const sendAnnouncement = async ({ all, templateName, language, variables }) => {
    const count = all ? total : selected.length;
    if (!templateName) { toast.error("Choose an Announcement template first"); return; }
    if (!count) { toast.error("Select at least one participant"); return; }
    setAnnouncing(true);
    setAnnounceJob({ total: count, sent: 0, failed: 0, done: false });
    try {
      const res = await fetch(`${API}/admin/past-participants/send-announcement`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ all: !!all, ids: all ? [] : selected, template_name: templateName, language, variables }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");
      let job = await pollJob(data.job_id);
      while (job && !job.done) {
        setAnnounceJob(job);
        await new Promise((r) => setTimeout(r, 1500));
        job = await pollJob(data.job_id);
      }
      if (job) {
        setAnnounceJob(job);
        if (job.sent) toast.success(`Announcement sent on WhatsApp to ${job.sent} of ${job.total} participant(s)`);
        if (job.failed) toast.error(`${job.failed} message(s) failed — see the status chips in the table for the reason`, { duration: 8000 });
      }
      fetchItems(q.trim(), page, filters);
    } catch (err) {
      setAnnounceJob(null);
      toast.error(err.message || "Could not send announcement", { duration: 8000 });
    } finally { setAnnouncing(false); }
  };

  return { items, total, q, setQ, loading, deletingId, exporting, exportExcel, remove, selected, toggleSelect, toggleAll, clearSelection, sendAnnouncement, announcing, announceJob, page, setPage, pageInfo, uploadCsv, uploading, filters, setFilter, clearFilters, editing, setEditing, editForm, setEditForm, startEdit, saveEdit, saving, refresh: () => fetchItems(q.trim(), page, filters) };
};
